import { z } from 'zod';
import {
  classeActif,
  codeScenario,
  roleUtilisateur,
  statutEcheance,
  statutKyc,
  statutSimulation,
  typeEcheance,
} from './enums.js';

// Entity schemas (modele-donnees). UUIDs and timestamps are generated in the database;
// amounts are exact numeric. Nullability follows the data model.

const uuid = z.string().uuid();
const horodatage = z.string().datetime({ offset: true });

export const organisation = z.object({
  id: uuid,
  nom: z.string().min(1),
  frais_gestion_defaut: z.number().min(0).max(1),
});
export type Organisation = z.infer<typeof organisation>;

export const profil = z.object({
  user_id: uuid,
  organisation_id: uuid,
  role: roleUtilisateur,
  prenom: z.string().min(1),
  nom: z.string().min(1),
  actif: z.boolean(),
});
export type Profil = z.infer<typeof profil>;

export const client = z.object({
  id: uuid,
  organisation_id: uuid,
  civilite: z.string().min(1),
  prenom: z.string().min(1),
  nom: z.string().min(1),
  date_naissance: z.string().date(),
  date_entree_relation: z.string().date(),
  patrimoine_estime: z.number().min(0),
  objectif_annee: z.number().int().min(2000).max(2100),
  profil_investisseur: z.string().min(1),
  note_risque: z.number().int().min(1).max(10),
  statut_kyc: statutKyc,
  kyc_valide_jusqu_au: z.string().date().nullable(),
  /** Comment of the last KYC renewal; absent on rows written before migration 0010. */
  kyc_commentaire: z.string().nullable().optional(),
  archive: z.boolean(),
  cree_le: horodatage,
  modifie_le: horodatage,
});
export type Client = z.infer<typeof client>;

export const allocation = z.object({
  simulation_id: uuid,
  classe: classeActif,
  poids: z.number().min(0).max(100),
});
export type Allocation = z.infer<typeof allocation>;

export const simulation = z.object({
  id: uuid,
  client_id: uuid,
  organisation_id: uuid,
  libelle: z.string().min(1),
  statut: statutSimulation,
  horizon_annees: z.number().int().min(1).max(40),
  objectif_capital: z.number().positive(),
  versement_initial: z.number().min(0),
  versement_mensuel: z.number().min(0),
  jour_prelevement: z.number().int().min(1).max(28),
  scenario_defaut: codeScenario,
  frais_gestion: z.number().min(0).max(1),
  hypotheses_version: z.number().int().positive(),
  /** Start of the plan, month zero of the projection; may predate the record. */
  date_debut: z.string().date().nullable().optional(),
  cree_le: horodatage,
  modifie_le: horodatage,
});
export type Simulation = z.infer<typeof simulation>;

export const echeance = z.object({
  id: uuid,
  simulation_id: uuid,
  date_echeance: z.string().date(),
  type: typeEcheance,
  montant: z.number().nullable(),
  statut: statutEcheance,
  commentaire: z.string().nullable(),
  /** Published progress of the reference screen; RG-13 applies when absent. */
  progression_reference: z.number().int().min(0).max(100).nullable().optional(),
});
export type Echeance = z.infer<typeof echeance>;

export const hypothesesScenario = z.object({
  organisation_id: uuid,
  version: z.number().int().positive(),
  scenario: codeScenario,
  rendement_brut_annuel: z.number(),
  actif: z.boolean(),
});
export type HypothesesScenario = z.infer<typeof hypothesesScenario>;

export const document = z.object({
  id: uuid,
  client_id: uuid,
  simulation_id: uuid.nullable(),
  type: z.string().min(1),
  chemin_storage: z.string().min(1),
  taille_octets: z.number().int().nonnegative(),
  genere_par: uuid,
  /** Published progress of the reference screen; RG-13 applies when absent. */
  progression_reference: z.number().int().min(0).max(100).nullable().optional(),
  cree_le: horodatage,
});
export type Document = z.infer<typeof document>;

export const notification = z.object({
  id: uuid,
  destinataire_id: uuid,
  titre: z.string().min(1),
  corps: z.string(),
  lu: z.boolean(),
  cree_le: horodatage,
});
export type Notification = z.infer<typeof notification>;
